'use client'

import React, { ReactElement, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import HotelSelect from '@/components/hotel/HotelSelect'
import Button from '@/components/common/button/Button'
import { ButtonType } from '@/components/common/button/ButtonType'
import { Room, WizardAvailability, WizardOffers, WizardOffersDate } from '@/types/Misc'
import { fetchWizardAvailability, fetchWizardOffers } from '@/lib/frontent-api'
import { ConfigContext } from '@/components/common/ConfigContext'
import ShowDetailsButton from '@/components/hotel/ShowDetailsButton'
import LoadingIndicator from '@/components/elements/LoadingIndicator'
import { useTranslations } from 'next-intl'
import ClockIcon from '@/icons/font-awesome/sharp-light/clock.svg'
import SpinnerThirdIcon from '@/icons/font-awesome/sharp-light/spinner-third.svg'
import {
  dayIsOutsideBookingWindow,
  isSameOrAfterDay,
  isSameOrBeforeDay,
  isTodayOrTomorrow,
  today,
  todayPlusTwoDays,
  useDateFnsLocale,
} from '@/lib/utils/dateUtils'
import { SHOW_ROOM_DETAILS_EVENT } from '@/components/hotel/constants'
import { HotelPageContext } from '@/components/hotel/HotelPageContext'
import {
  ClassNames,
  DayContentProps,
  DayPicker,
  DropdownProps,
  useNavigation,
} from 'react-day-picker'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { isSameDay } from 'date-fns/isSameDay'
import { isSameMonth } from 'date-fns/isSameMonth'

import { getDay } from 'date-fns/getDay'
import { startOfMonth } from 'date-fns/fp/startOfMonth'
import { isAfter } from 'date-fns/isAfter'
import { isBefore } from 'date-fns/isBefore'
import { endOfMonth } from 'date-fns/endOfMonth'
import { getMonth } from 'date-fns/getMonth'
import { max } from 'date-fns/max'
import { getYear } from 'date-fns/getYear'
import uniqBy from 'lodash/uniqBy'
import { type Month } from 'date-fns/types'
import { min } from 'date-fns'
import { isInTestGroup } from '@/lib/ab-tests/test-variants'
import { TestVariant } from '@/lib/ab-tests/test-definitions'
import { Domain } from '../../domain-config/domain-config'

const SUNDAY = 0

enum DayColor {
  ON_REQUEST = 'bg-calendar-on-request text-calendar-on-request-complement',
  PRIMARY = 'bg-primary text-calendar-booked-complement',
  BOOKABLE = 'bg-calendar-bookable text-calendar-bookable-complement',
  WHITE = 'bg-white',
}

function RoomDropdownDetails(props: { roomCode: string; rooms: Room[] }): ReactElement | undefined {
  return (
    <div className="mb-2 text-sm">
      <div className="flex flex-row flex-wrap gap-x-3 gap-y-1 leading-tight">
        {props.rooms
          .find((r) => r.code === props.roomCode)
          ?.features.map((f) => (
            <div key={f.group}>
              {f.group}
            </div> /* changed to f.group, since f.text does not exist on FeatureGroup [] */
          ))}
      </div>{' '}
    </div>
  )
}

function ShowRoomDetailsButton(props: {
  roomCode: string
  rooms: Room[]
}): ReactElement | undefined {
  function showRoomDetailsEvent(roomCode: string): void {
    window.dispatchEvent(
      new CustomEvent(SHOW_ROOM_DETAILS_EVENT, {
        detail: {
          code: roomCode,
        },
      }),
    )
  }

  function openHotelModal(key: string): void {
    const room = props.rooms.find((r) => r.code === key)
    if (room) {
      showRoomDetailsEvent(room.code)
    }
  }

  return (
    <ShowDetailsButton
      onClick={(e) => {
        e.stopPropagation()
        openHotelModal(props.roomCode)
      }}
      colorLight={true}
    />
  )
}

function DirectBookingButtonLabel(props: { additionalClassName: string; showFlight: boolean }) {
  const t = useTranslations('Hotel.Programs.ProgramCalender')
  return (
    <div className={props.additionalClassName}>
      <div className="text-xl font-bold leading-5">{t('bookingButtonLabel')}</div>
      {props.showFlight && (
        <div className="text-xxs font-normal leading-4">{t('buttonLabelWithoutFlight')}</div>
      )}
    </div>
  )
}

function BookingOnRequestLabel(props: { additionalClassName: string; showFlight: boolean }) {
  const t = useTranslations('Hotel.Programs.ProgramCalender')
  return (
    <div className={props.additionalClassName}>
      <div className="text-xl font-bold leading-5">{t('bookingRequestButtonLabel')}</div>
      {props.showFlight && (
        <div className="text-xxs font-normal leading-4">
          {t('bookingRequestButtonLabelFlightPossible')}
        </div>
      )}
    </div>
  )
}

function ErrorMessage(props: { message: ReactNode }) {
  return <div className="mb-2 w-full text-xs font-medium text-highlight-red">{props.message}</div>
}

function CheckAvailabilityLabel(props: { textColor?: string }) {
  const t = useTranslations('Hotel.Programs.ProgramCalender')
  return (
    <div
      className={classNames(
        'flex h-full items-center text-lg font-normal leading-5',
        props.textColor,
      )}
    >
      {t('programCalendarCheckAvailability')}
    </div>
  )
}

function LegendItem(props: { label: string; color: DayColor }) {
  return (
    <div className="flex items-end gap-2">
      <div className={classNames('h-3.5 w-3.5 rounded-md', props.color)} />
      <div className="whitespace-nowrap text-sm leading-none">{props.label}</div>
    </div>
  )
}
export const dayPickerClassNames: ClassNames = {
  root: 'w-full',
  head: 'custom-head',
  head_cell: 'w-[14.285714285714286%] h-12 p-[2px] text-center',
  day: 'w-full h-full block items-center justify-center',
  cell: 'm-0 h-12 p-[0.5px] tiny:p-[2px] cursor-pointer',
  day_disabled: 'text-stone-300',
  // day_selected: '',
  months: 'w-full',
  month: 'w-full',
  table: 'w-full mt-4 table-fixed',
  caption: 'flex justify-center relative mx-3 my-2',
  nav_button_previous: 'absolute left-0 top-1/2 -translate-y-1/2',
  nav_button_next: 'absolute right-0 top-1/2 -translate-y-1/2',
  caption_dropdowns: 'flex',
  // caption_label: 'hidden',
  // dropdown: 'appearance-none p-0 m-0',
  dropdown_month: 'flex',
  dropdown_year: 'flex',
}

function MinimumStayMessage(props: { offers: WizardOffers; displayedMonth: Date }) {
  const t = useTranslations('Hotel.Programs.ProgramCalender')

  const entireMonthNoOffer = !props.offers?.dates.find(
    (date) => getMonth(date.date) === getMonth(props.displayedMonth),
  )

  const minimumNumberOfNights =
    entireMonthNoOffer &&
    props.offers.calendarHints.find((hint) => hint.month === getMonth(props.displayedMonth) + 1)
      ?.minDuration

  const minStayMessage =
    minimumNumberOfNights &&
    t('minimumStayMessage', {
      numberNights: minimumNumberOfNights,
    })

  return (
    <>
      {minStayMessage && (
        <div className="absolute left-0 right-0 top-[84px] z-10 flex h-[300px] items-center justify-center bg-white bg-opacity-50">
          <div className="mx-4 rounded-md bg-white p-2 py-1 text-center text-md text-stone-600 opacity-100 shadow-even">
            {minStayMessage}
          </div>
        </div>
      )}
    </>
  )
}

export default function ProgramCalender(props: {
  prevIcon: ReactNode
  nextIcon: ReactNode
  programCode: string
  hotelCode: string
  bookingCode: string
  rooms: Room[]
}): ReactElement {
  const locale = useContext(ConfigContext)!.locale
  const { getProgram, programs } = useContext(HotelPageContext)
  const program = getProgram(props.programCode)!
  const [beds, setBeds] = useState<number>(program.selectedBeds)
  const [duration, setDuration] = useState<number>(program.selectedDuration)
  const [roomCode, setRoomCode] = useState<string>(program.selectedRoomCode)
  const [mealPlanCode, setMealPlanCode] = useState<number>(program.selectedMealPlanCode)
  const [offers, setOffers] = useState<WizardOffers | null>(null)
  const [offersDates, setOffersDates] = useState<Record<string, WizardOffersDate>>({})
  const [availableMonths, setAvailableMonths] = useState<Date[]>([])
  const [manuallyChangedDisplayedMonth, setManuallyChangedDisplayedMonth] = useState(false)
  const [displayedMonth, setDisplayedMonth] = useState<Date | undefined>(
    program.selectedDateFrom ? new Date(program.selectedDateFrom) : todayPlusTwoDays(),
  )
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    program.selectedDateFrom ? new Date(program.selectedDateFrom) : todayPlusTwoDays(),
  )
  const [availability, setAvailability] = useState<WizardAvailability | null>(null)
  const [fetchingAvailability, setFetchingAvailability] = useState<boolean>(false)
  const [initialLoad, setInitialLoad] = useState<boolean>(true)

  const containerRef = useRef<HTMLDivElement>(null)
  const currency = useContext(HotelPageContext)!.currency
  const domain = useContext(ConfigContext)!.domain
  const selectedOffer = getSelectedOffer()
  const t = useTranslations('Hotel.Programs.ProgramCalender')
  const bedsChangedRef = useRef(false)
  const dateLocale = useDateFnsLocale(locale)
  const showNewBookingPage =
    isInTestGroup(TestVariant.NewBookingPage) && domain !== Domain.FITREISEN_DE

  useEffect(() => {
    setDuration(program.selectedDuration)
    setBeds(program.selectedBeds)
    setRoomCode(program.selectedRoomCode)
    setMealPlanCode(program.selectedMealPlanCode)
    setSelectedDate(new Date(program.selectedDateFrom))
  }, [programs])

  useEffect(() => {
    void fetchWizardOffers({
      programCode: program.programCode,
      beds: beds.toString(),
      roomCode: roomCode,
      duration: duration.toString(),
      mealPlanCode: mealPlanCode.toString(),
      currency: currency,
      dateFrom: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined,
      domain: domain,
    })
      .then((wizardOffers) => {
        if (updateRoomCodeIfNeeded(wizardOffers)) {
          return
        }

        setOffers(wizardOffers)
        setOffersDates(
          wizardOffers.dates.reduce((acc, date) => {
            acc[date.date.replace('T00:00:00', '')] = date
            return acc
          }, {}),
        )
        const months =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          uniqBy(
            wizardOffers.dates.map((date) => startOfMonth(new Date(date.date))),
            (d) => `${getMonth(d)}${getYear(d)}`,
          )

        setAvailableMonths([...months])

        if (!manuallyChangedDisplayedMonth) {
          setDisplayedMonth(selectedDate)
        }
      })
      .catch(() => false)
  }, [beds, duration, roomCode, mealPlanCode, selectedDate])

  useEffect(() => {
    if (!selectedOffer) {
      setAvailability(null)
      return
    }

    setFetchingAvailability(true)

    void fetchWizardAvailability({
      price: selectedOffer.price.toString(),
      currency: currency,
      offerId: selectedOffer.offerId,
      domain: domain,
    })
      .then((wizardAvailability) => {
        setAvailability(wizardAvailability)
        setFetchingAvailability(false)
        setInitialLoad(false)
      })
      .catch(() => false)
  }, [selectedOffer?.offerId])

  function getSelectedOffer(): WizardOffersDate | null | undefined {
    return selectedDate && findInDates(selectedDate, offersDates)
  }

  function getPriceChangeDuration(duration: number):
    | {
        priceChange: number
        priceChangeAsString: string
      }
    | undefined {
    const durationOption = offers?.durations?.find((d) => d.value === duration)
    return (
      durationOption && {
        priceChangeAsString: durationOption.priceChangeAsString,
        priceChange: durationOption.priceChange,
      }
    )
  }

  function getPriceChangeBeds(bed: number):
    | {
        priceChange: number
        priceChangeAsString: string
      }
    | undefined {
    const bedOption = offers?.beds?.find((b) => b.value === bed)
    return (
      bedOption && {
        priceChangeAsString: bedOption.priceChangeAsString,
        priceChange: bedOption.priceChange,
      }
    )
  }

  function updateRoomCodeIfNeeded(wizardOffers: WizardOffers) {
    const roomCodes = wizardOffers.roomCodes.map((rc) => rc.value)

    if (roomCodes.length > 0 && !roomCodes.includes(roomCode)) {
      const cheapestRoomCode = wizardOffers.roomCodes.sort((c) => c.priceChange)[0].value
      setRoomCode(cheapestRoomCode)
      return true
    }

    return false
  }

  function getPriceChangeRoomCode(roomCode: string):
    | {
        priceChange: number
        priceChangeAsString: string
      }
    | undefined {
    const roomCodeOption = offers?.roomCodes?.find((r) => r.value === roomCode)
    return (
      roomCodeOption && {
        priceChangeAsString: roomCodeOption.priceChangeAsString,
        priceChange: roomCodeOption.priceChange,
      }
    )
  }

  function getPriceChangeMealPlanCode(mealPlanCode: number):
    | {
        priceChange: number
        priceChangeAsString: string
      }
    | undefined {
    const mealPlanCodeOption = offers?.mealPlanCodes?.find((m) => m.value === mealPlanCode)
    return (
      mealPlanCodeOption && {
        priceChangeAsString: mealPlanCodeOption.priceChangeAsString,
        priceChange: mealPlanCodeOption.priceChange,
      }
    )
  }

  function newBookingUrl(direct: boolean) {
    if (selectedOffer) {
      const params = new URLSearchParams({
        programCode: program.programCode,
        roomCode: roomCode,
        mealPlanCode: mealPlanCode.toString(),
        dateFrom: `${selectedOffer.date}.000Z`,
        duration: duration.toString(),
        beds: beds.toString(),
        price: selectedOffer.price.toString(),
      })

      return `/checkout/${direct ? 'booking' : 'request'}/?${params.toString()}`
    }

    return ''
  }

  function bookingUrl(direct: boolean) {
    if (showNewBookingPage) {
      return newBookingUrl(direct)
    }

    return legacyBookingUrl(direct)
  }

  function legacyBookingUrl(direct: boolean) {
    if (selectedOffer) {
      return `/ibe/${direct ? 'booking' : 'request'}/${selectedOffer.offerId}`
    }

    return ''
  }

  function directBookingIsThePrimaryAction() {
    return !availability || availability?.bookingEnabled
  }

  function offerAvailable() {
    return (
      fetchingAvailability ||
      (availability && (availability.bookingEnabled || availability.bookingRequestEnabled))
    )
  }

  function dayIsBlocked(day: Date): boolean {
    if (!offers || dayIsOutsideBookingWindow()(day)) {
      return true
    }

    return !findInDates(day, offersDates)
  }

  function isDayDisabled(date: Date) {
    return (
      dayIsOutsideBookingWindow()(date) ||
      !Object.keys(offersDates).includes(format(date, 'yyyy-MM-dd'))
    )
  }

  const modifiers = {
    disabled: isDayDisabled,
  }

  const fromDate = offers?.dates.length ? new Date(offers.dates[0].date) : new Date()
  const lastAvailableDate = offers?.dates.length
    ? new Date(offers.dates[offers?.dates.length - 1].date)
    : new Date()
  const lastTravelDate = selectedDate ? addDays(selectedDate, duration) : today()
  const toDate = max([lastAvailableDate, lastTravelDate])

  function manuallyChangeDisplayedMonth(newMonth: Date) {
    setDisplayedMonth(newMonth)
    setManuallyChangedDisplayedMonth(true)
  }

  return (
    <div ref={containerRef} className="hidden px-5 pb-5 open:block lg:pb-10">
      <div className="mb-6 text-3xl font-medium">{t('programCalenderTitle')}</div>
      <div className="border-b-black-24 mb-4 flex flex-col gap-8 border-b-[1px] lg:flex-row">
        <div>
          <div className="mb-4 font-semibold">1. {t('programCalenderStepOne')}</div>
          <div className="mb-2 flex flex-col gap-2 tiny:grid tiny:grid-cols-2 lg:flex lg:flex-col">
            <div className="w-full lg:w-[368px]">
              <HotelSelect
                options={program.beds.map((bed) => ({
                  value: bed.toString(),
                  label: `${bed} ${t('guest', { count: bed })}`,
                  ...getPriceChangeBeds(bed),
                  optionAvailable: !!getPriceChangeBeds(bed),
                }))}
                selectedValue={beds.toString()}
                onChange={(value) => {
                  bedsChangedRef.current = true
                  setBeds(+value)
                }}
                onReset={() => false}
                label={t('peopleSelectLabel')}
                hideUnavailableOptions={false}
              />
            </div>
            <div className="w-full lg:w-[368px]">
              <HotelSelect
                options={program.roomCodes.map((roomCode) => ({
                  value: roomCode.key.toString(),
                  label: roomCode.value,
                  ...getPriceChangeRoomCode(roomCode.key.toString()),
                  optionAvailable: !!getPriceChangeRoomCode(roomCode.key.toString()),
                  details: <RoomDropdownDetails rooms={props.rooms} roomCode={roomCode.key} />,
                  action: <ShowRoomDetailsButton rooms={props.rooms} roomCode={roomCode.key} />,
                }))}
                selectedValue={roomCode.toString()}
                onChange={(value) => setRoomCode(value)}
                onReset={() => false}
                label={t('roomSelectLabel')}
                hideUnavailableOptions={true}
              />
            </div>
            <div className="w-full lg:w-[368px]">
              <HotelSelect
                options={program.durations.map((duration) => ({
                  value: duration.toString(),
                  label: `${duration} ${t('durationOptionsNight', { count: duration })}`,
                  ...getPriceChangeDuration(duration),
                  optionAvailable: !!getPriceChangeDuration(duration),
                }))}
                selectedValue={duration.toString()}
                onChange={(value) => setDuration(+value)}
                onReset={() => false}
                label={t('durationSelectLabel')}
                hideUnavailableOptions={false}
              />
            </div>
            <div className="w-full lg:w-[368px]">
              <HotelSelect
                options={program.mealPlanCodes.map((mealPlanCodeOption) => ({
                  value: mealPlanCodeOption.key.toString(),
                  label: mealPlanCodeOption.value,
                  ...getPriceChangeMealPlanCode(mealPlanCodeOption.key),
                  optionAvailable: !!getPriceChangeMealPlanCode(mealPlanCodeOption.key),
                }))}
                selectedValue={mealPlanCode.toString()}
                onChange={(value) => setMealPlanCode(+value)}
                onReset={() => false}
                label={t('mealSelectLabel')}
                hideUnavailableOptions={false}
              />
            </div>
            {availability?.bookingCode && (
              <div className="ml-1 w-full text-xxs leading-none text-stone-400 lg:w-[368px]">
                {t('bookingCode')}: {availability.bookingCode}
              </div>
            )}
          </div>
        </div>
        <div className="mx-[-18px] grow tiny:mx-0">
          <div className="mx-[18px] mb-4 font-semibold tiny:mx-0">
            2. {t('programCalenderStepTwo')}
          </div>
          <div className="relative">
            {offers && displayedMonth && (
              <MinimumStayMessage offers={offers} displayedMonth={displayedMonth} />
            )}
            {offers?.dates.length === 0 && (
              <div>
                <div className="absolute left-0 top-0 z-10 h-full w-full bg-white opacity-50" />
                <div className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 transform bg-white p-3">
                  {<ErrorMessage message={t('noOffersAvailable')} />}
                </div>
              </div>
            )}
            {offers && dateLocale && (
              <DayPicker
                mode="single"
                className="h-[380px]"
                captionLayout="dropdown-buttons"
                numberOfMonths={1}
                components={{
                  DayContent: (props) => (
                    <DateTime
                      {...props}
                      dates={offersDates}
                      selectedDate={selectedDate}
                      duration={duration}
                      unavailable={dayIsBlocked(props.date)}
                      offers={offers}
                    />
                  ),
                  Dropdown: (dropDownProps) => (
                    <CalenderSelect
                      {...dropDownProps}
                      availableMonths={availableMonths}
                      travelDuration={duration}
                      selectedDate={selectedDate}
                    />
                  ),
                  CaptionLabel: () => <></>,
                }}
                classNames={dayPickerClassNames}
                modifiers={modifiers}
                fromDate={fromDate}
                toDate={toDate}
                selected={selectedDate}
                onSelect={(v) => v && setSelectedDate(v)}
                locale={dateLocale}
                onMonthChange={manuallyChangeDisplayedMonth} // Optional: handle month change (e.g., via the built-in navigation)
                // defaultMonth={displayedMonth}
                month={displayedMonth}
              />
            )}
            <div className="mb-4 ml-[24px] mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pb-2 lg:mt-6">
              <LegendItem
                label={t('programCalenderLegendBookableDirectly')}
                color={DayColor.BOOKABLE}
              />
              <LegendItem
                label={t('programCalenderLegendUponRequest')}
                color={DayColor.ON_REQUEST}
              />
              <LegendItem
                label={t('programCalenderLegendYourSelection')}
                color={DayColor.PRIMARY}
              />
            </div>
            {!offers && (
              <div className="py-60 lg:py-40">
                <LoadingIndicator additionalClassName="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="h-min-[144px] relative flex justify-end">
        <div className="flex w-full flex-col items-end sm:w-auto">
          <div className="flex">
            <span className={classNames('mr-2 text-3xl font-medium', { 'mb-2': beds === 1 })}>
              {availability ? (
                availability.priceString
              ) : (
                <>
                  {t('programPricePrefix')} {program.price}
                </>
              )}
            </span>
            {beds > 1 && (
              <span className="flex items-end text-sm font-normal">
                {t('programCalenderPerPersonAbbreviation')}
              </span>
            )}
          </div>
          {beds > 1 && availability && (
            <div className="mb-1.5 font-medium text-stone-600">
              <span className="text-xxs">{t('programCalenderTotalPrice')}</span>{' '}
              <span>{availability.totalPriceString}</span>
            </div>
          )}
          <div className="relative flex w-full flex-col gap-x-5 sm:w-auto sm:flex-row">
            {offerAvailable() && (
              <>
                {availability?.bookingRequestEnabled &&
                  availability.bookingEnabled &&
                  !fetchingAvailability && (
                    <Button
                      buttonType={ButtonType.SECONDARY}
                      className={classNames(
                        'relative mb-3 flex h-[52px] w-full flex-row items-center justify-center gap-4 border-[1px] border-primary px-6 py-2 text-stone-600 tiny:px-14 sm:mb-2 sm:w-auto lg:px-14',
                        { 'cursor-default': !availability || fetchingAvailability },
                      )}
                      onClick={
                        fetchingAvailability
                          ? undefined
                          : () => (window.location.href = bookingUrl(false))
                      }
                    >
                      <BookingOnRequestLabel
                        additionalClassName={fetchingAvailability ? 'text-white' : 'text-primary'}
                        showFlight={availability.flightPossible}
                      />
                    </Button>
                  )}
                <Button
                  buttonType={ButtonType.PRIMARY}
                  className={classNames(
                    'relative mb-2 box-border flex h-[52px] w-full flex-row items-center justify-center gap-4 py-2 text-white sm:w-auto',
                    {
                      'cursor-default opacity-40': !availability || fetchingAvailability,
                      'px-6 tiny:px-14': !fetchingAvailability,
                      'px-2': fetchingAvailability,
                    },
                  )}
                  onClick={
                    selectedOffer && !fetchingAvailability
                      ? () => (window.location.href = bookingUrl(directBookingIsThePrimaryAction()))
                      : undefined
                  }
                >
                  <SpinnerThirdIcon
                    className={classNames('h-7 animate-spin fill-white', {
                      hidden: !fetchingAvailability,
                      block: fetchingAvailability,
                    })}
                  />
                  {fetchingAvailability && <CheckAvailabilityLabel />}
                  {offerAvailable() &&
                    directBookingIsThePrimaryAction() &&
                    !fetchingAvailability && (
                      <DirectBookingButtonLabel
                        additionalClassName={fetchingAvailability ? 'text-primary' : 'text-white'}
                        showFlight={
                          availability
                            ? availability.flightPossible && availability.bookingRequestEnabled
                            : false
                        }
                      />
                    )}
                  {!availability?.bookingEnabled &&
                    availability?.bookingRequestEnabled &&
                    !fetchingAvailability && (
                      <BookingOnRequestLabel
                        additionalClassName={fetchingAvailability ? 'text-primary' : 'text-white'}
                        showFlight={availability.flightPossible}
                      />
                    )}
                </Button>
              </>
            )}
            {!offerAvailable() && (
              <div>
                <ErrorMessage message={t('offerFullyBooked')} />
              </div>
            )}
          </div>
          {availability?.priceChange && (
            <div className="mb-2 mt-0.5 text-xs font-medium text-stone-600">
              {t('priceChangeInformation')}
            </div>
          )}
          {offers?.dates.length !== 0 && !availability && !initialLoad && (
            <ErrorMessage message={t('offerNotAvailable')} />
          )}
          {availability?.reservationPossible && (
            <div className="relative inline pl-[28px] text-right font-medium text-stone-600">
              <ClockIcon className="mr-2 inline h-5 fill-black-64" />
              {t('cancellationInformation')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function findInDates(
  day: Date,
  days: Record<string, WizardOffersDate>,
): WizardOffersDate | undefined {
  return days[format(day, 'yyyy-MM-dd')]
}

interface DayInfo {
  price: string
  color: DayColor
  withinTravelPeriod: boolean
  first?: boolean
  last?: boolean
}

function DateTime(
  props: DayContentProps & {
    dates: Record<string, WizardOffersDate>
    selectedDate: Date | undefined
    duration: number
    unavailable: boolean
    offers: WizardOffers | null
  },
) {
  const fallback = { price: '', color: DayColor.WHITE, selected: false }

  function isSelectedDay(selectedDate: Date | null | undefined, currentDate: Date) {
    return selectedDate && isSameDay(currentDate, selectedDate)
  }

  const selectedDay = isSelectedDay(props.selectedDate, props.date)

  function getDayColor(day: WizardOffersDate, withinTravelPeriod: boolean): DayColor {
    if (selectedDay || withinTravelPeriod) {
      return DayColor.PRIMARY
    }

    if (day.allotment) {
      return DayColor.BOOKABLE
    } else {
      return DayColor.ON_REQUEST
    }
  }

  function isWithinRange(
    day: Date,
    selectedDate: Date | null | undefined,
    duration: number,
  ): boolean {
    if (!selectedDate) {
      return false
    }

    const endDate = addDays(selectedDate, duration)

    return isSameOrAfterDay(day, selectedDate) && isSameOrBeforeDay(day, endDate)
  }

  function getDayInfo(): DayInfo {
    const date = findInDates(props.date, props.dates)

    const withinTravelPeriod = !!(
      isWithinRange(props.date, props.selectedDate, props.duration) &&
      props.selectedDate &&
      findInDates(props.selectedDate, props.dates)
    )

    const dateIndependentData = {
      withinTravelPeriod: withinTravelPeriod,
      first: !!(props.selectedDate && isSameDay(props.date, props.selectedDate)),
      last: !!(
        props.selectedDate && isSameDay(addDays(props.selectedDate, props.duration), props.date)
      ),
      price: (date && !isTodayOrTomorrow(new Date(date.date)) && date?.priceString) || '',
    }

    if (withinTravelPeriod) {
      return {
        ...fallback,
        ...dateIndependentData,
        color: DayColor.PRIMARY,
      }
    }

    if (date && !props.unavailable) {
      return {
        color: getDayColor(date, withinTravelPeriod),
        ...dateIndependentData,
      }
    } else {
      return { ...fallback, ...dateIndependentData }
    }
  }

  const info = getDayInfo()

  function marginRight(): boolean {
    return info.withinTravelPeriod && getDay(props.date) !== SUNDAY && !info.last
  }

  function roundedRight(): boolean {
    return !info.withinTravelPeriod || (info.withinTravelPeriod && !!info.last)
  }

  function roundedLeft(): boolean {
    return !info.withinTravelPeriod || (info.withinTravelPeriod && !!info.first)
  }

  return (
    <div
      className={classNames(info.color, 'z-90 relative flex h-full flex-col items-center', {
        [DayColor.PRIMARY]:
          info.withinTravelPeriod &&
          props.selectedDate &&
          findInDates(props.selectedDate, props.dates),
        'rounded-r-sm md:rounded-r-md': roundedRight(),
        'w-[calc(100%+8px)] pr-[8px]': marginRight(),
        'rounded-l-sm md:rounded-l-md': roundedLeft(),
      })}
    >
      <div
        className={classNames(
          'flex h-1/2 w-full grow items-center justify-center font-medium leading-none',
        )}
      >
        {props.date.getDate()}
      </div>
      <div
        className={classNames(
          'flex h-1/2 w-full grow items-center justify-center text-xxs leading-none sm:text-xs sm:leading-none',
          { 'text-calendar-booked-days-within-period': info.withinTravelPeriod && !selectedDay },
        )}
      >
        {info.price}
      </div>
    </div>
  )
}

function CalenderSelect(
  props: DropdownProps & {
    availableMonths: Date[]
    travelDuration: number
    selectedDate: Date | undefined
  },
) {
  const { goToMonth, currentMonth: selectedMonth } = useNavigation()
  const locale = useContext(ConfigContext)!.locale
  const dateLocale = useDateFnsLocale(locale)
  const currentMonth = startOfMonth(new Date())
  const months = getMonthsForCalenderDropdown()

  useEffect(() => {
    if (months.findIndex((month) => isSameMonth(month, selectedMonth)) === -1) {
      goToMonth(months[months.length - 1])
    }
  }, [months, selectedMonth])

  if (props.name !== 'months') {
    return <></>
  }

  if (!dateLocale) {
    return <></>
  }

  function monthShowingAPossibleTravelStartDateOrEndDate(month: Date) {
    // return props.availableMonths.some((availableMonth) => isSameMonth(availableMonth, month))
    const smallestAvailableMonth = min(props.availableMonths)
    const largestAvailableMonth = max([
      max(props.availableMonths),
      props.selectedDate ? addDays(props.selectedDate, props.travelDuration) : today(),
    ])

    // month is equal or large than the smallest available month and equal or smaller than the largest available month
    return (
      isAfter(startOfMonth(month), addMonths(startOfMonth(smallestAvailableMonth), -1)) &&
      isBefore(
        startOfMonth(month),
        addMonths(startOfMonth(addDays(largestAvailableMonth, props.travelDuration)), 1),
      )
    )
  }

  function getMonthsForCalenderDropdown() {
    return (
      Array.from({ length: 14 }, (_, i) => addMonths(currentMonth, i))
        // .filter(monthIsWithingEarliestAndLatest)
        .filter(monthShowingAPossibleTravelStartDateOrEndDate)
        .map(startOfMonth)
    )
  }

  const options = months.map((month) => ({
    label: `${dateLocale.localize.month(month.getMonth() as Month)}`,
    value: endOfMonth(month).toISOString(),
    optionAvailable:
      props.availableMonths.filter((availableMonth) => isSameMonth(availableMonth, month)).length >
      0,
  }))

  return (
    <HotelSelect
      options={options}
      selectedValue={endOfMonth(selectedMonth).toISOString()}
      onChange={(value) => goToMonth(new Date(value))}
      hideUnavailableOptions={false}
      onReset={() => false}
      selectedYear={selectedMonth.getFullYear().toString()}
      className="h-7 w-44 tiny:w-52"
    />
  )
}
